import React, { useEffect, useLayoutEffect, useRef } from "react";

import { chatText } from "../messages";
import { ChatComposer } from "./ChatComposer";
import { TypingIndicator } from "./TypingIndicator";
import { MessageBubble } from "./MessageBubble";
import { QuickReplies } from "./QuickReplies";
import { RecipientPicker } from "./RecipientPicker";
import { RecipientFormCard } from "./RecipientFormCard";
import { SecurePinCard } from "./SecurePinCard";
import { BankFundingCard } from "./BankFundingCard";
import { CardFundingCard } from "./CardFundingCard";
import { TransferStatusCard } from "./TransferStatusCard";

export function ChatWindow({
  messages,
  quickReplies,
  sendMessage,
  bottomRef,

  activeCard,
  context,

  createRecipient,
  cancelRecipientCreation,
  isCreatingRecipient,

  confirmWithPin,

  isConfirmingTransfer,
  isPreparingTransfer,

  fundingInstructions,
  isLoadingFundingInstructions,
  fundingInstructionsNotReady,
  fundingInstructionsError,

  transferDetail,

  activeStep = null,
  isPending = false,
  onRetry,
  historyHasMore = false,
  onLoadEarlier,
  isLoadingEarlier = false,
  composerPlaceholder,

  embedded = false,
}) {
  const scrollRef = useRef(null);
  const nearBottomRef = useRef(true);
  const prependAnchorRef = useRef(null);

  function onScroll() {
    const element = scrollRef.current;
    if (!element) return;
    nearBottomRef.current =
      element.scrollHeight - element.scrollTop - element.clientHeight < 80;
  }

  /*
   * Follow new messages only while the customer is at the bottom, and keep
   * the view still when earlier messages are prepended above it.
   */
  useLayoutEffect(() => {
    const element = scrollRef.current;
    const anchor = prependAnchorRef.current;

    if (element && anchor !== null) {
      element.scrollTop = element.scrollHeight - anchor;
      prependAnchorRef.current = null;
    }
  }, [messages]);

  useEffect(() => {
    if (nearBottomRef.current && prependAnchorRef.current === null) {
      bottomRef?.current?.scrollIntoView?.({ behavior: "smooth" });
    }
  }, [messages, activeCard, activeStep, bottomRef]);

  function loadEarlier() {
    const element = scrollRef.current;
    prependAnchorRef.current = element
      ? element.scrollHeight - element.scrollTop
      : null;
    onLoadEarlier?.();
  }

  const transferId =
    context?.transfer?.id || null;

  const showRecipientForm =
    activeCard?.type ===
    "recipient_form";

  const showPin =
    activeCard?.type ===
    "secure_pin";

  const showBankFunding =
    context?.phase ===
      "awaiting_bank_funding" &&
    context?.fundingMethod === "bank";

  const showCardFunding =
    context?.phase ===
      "awaiting_card_funding" &&
    context?.fundingMethod === "card";

  const showStatus =
    Boolean(transferId) &&
    Boolean(transferDetail);

  /*
   * Recipient selection uses the same deterministic
   * quick-reply values as before.
   *
   * We only swap the UI when the reply collection
   * contains the New recipient action.
   *
   * RecipientPicker then:
   * - keeps New recipient pinned
   * - makes existing recipients searchable
   * - preserves horizontal scrolling
   */
  const showRecipientPicker =
    quickReplies?.some((reply) => {
      const value = String(
        reply?.value || ""
      ).toLowerCase();

      return (
        value === "__new_recipient__" ||
        value === "new_recipient"
      );
    }) || false;

  return (
    <div
      className={`flex min-h-0 flex-col overflow-hidden bg-[#EFEAE2] ${
        embedded
          ? "h-full"
          : "h-[calc(100vh-8rem)] min-h-[620px] rounded-[28px] border border-[#0A4730]/10 shadow-xl"
      }`}
    >
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="min-h-0 flex-1 overflow-y-auto px-4 py-5 md:px-6"
        style={{
          backgroundImage:
            "radial-gradient(rgba(10,71,48,0.035) 1px, transparent 1px)",
          backgroundSize:
            "18px 18px",
        }}
      >
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-2.5">
          {historyHasMore && (
            <button
              type="button"
              onClick={loadEarlier}
              disabled={isLoadingEarlier}
              className="mx-auto rounded-full bg-white/80 px-4 py-1.5 text-xs font-medium text-[#0A4730] shadow-sm disabled:opacity-60"
            >
              {isLoadingEarlier
                ? chatText("chat.message.loading_earlier")
                : chatText("chat.message.load_earlier")}
            </button>
          )}

          {messages.map(
            (message) => (
              <MessageBubble
                key={message.id}
                message={message}
                onRetry={onRetry}
              />
            )
          )}

          {/* Inline create-recipient flow */}
          {showRecipientForm && (
            <div className="mt-2 flex justify-start">
              <RecipientFormCard
                createRecipient={
                  createRecipient
                }
                onCancel={
                  cancelRecipientCreation
                }
                isCreatingRecipient={
                  isCreatingRecipient
                }
              />
            </div>
          )}

          {/* PIN never enters messages[] */}
          {showPin && (
            <div className="mt-2 flex justify-start">
              <SecurePinCard
                fundingMethod={
                  context?.fundingMethod
                }
                isSubmitting={
                  isConfirmingTransfer ||
                  isPreparingTransfer
                }
                onConfirm={
                  confirmWithPin
                }
              />
            </div>
          )}

          {showBankFunding && (
            <div className="mt-2 flex justify-start">
              <BankFundingCard
                instructions={
                  fundingInstructions
                }
                isLoading={
                  isLoadingFundingInstructions
                }
                notReady={
                  fundingInstructionsNotReady
                }
                error={
                  fundingInstructionsError
                }
              />
            </div>
          )}

          {showCardFunding && (
            <div className="mt-2 flex justify-start">
              <CardFundingCard
                transferId={
                  transferId
                }
              />
            </div>
          )}

          {showStatus && (
            <div className="mt-2 flex justify-start">
              <TransferStatusCard
                transfer={
                  transferDetail
                }
              />
            </div>
          )}

          <TypingIndicator step={activeStep} />

          <div ref={bottomRef} />
        </div>
      </div>

      {quickReplies?.length >
        0 && (
        <div className="border-t border-black/5 bg-[#F7F5F0] px-3 py-2">
          {showRecipientPicker ? (
            <RecipientPicker
              replies={
                quickReplies
              }
              onSelect={
                sendMessage
              }
            />
          ) : (
            <QuickReplies
              replies={
                quickReplies
              }
              onSelect={
                sendMessage
              }
            />
          )}
        </div>
      )}

      <div className="border-t border-black/5 bg-[#F7F5F0]">
        <ChatComposer
          onSend={
            sendMessage
          }
          isPending={isPending}
          placeholderKey={composerPlaceholder}
        />
      </div>
    </div>
  );
}